import React, { useState } from 'react';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useStripeContext } from '../../../contexts/StripeContext';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const StripeCardForm = ({ onSubmit, isLoading, selectedTier }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [billingData, setBillingData] = useState({
    cardholderName: '',
    billingAddress: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'US'
    },
    saveCard: true,
    autoRenew: true
  });
  const [errors, setErrors] = useState({});
  const [cardErrors, setCardErrors] = useState({});

  const countryOptions = [
    { value: 'US', label: 'United States' },
    { value: 'CA', label: 'Canada' },
    { value: 'GB', label: 'United Kingdom' },
    { value: 'AU', label: 'Australia' },
    { value: 'DE', label: 'Germany' },
    { value: 'FR', label: 'France' },
    { value: 'JP', label: 'Japan' },
    { value: 'SG', label: 'Singapore' }
  ];

  const stateOptions = [
    { value: 'AL', label: 'Alabama' },
    { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' },
    { value: 'CA', label: 'California' },
    { value: 'FL', label: 'Florida' },
    { value: 'NY', label: 'New York' },
    { value: 'TX', label: 'Texas' },
    { value: 'WA', label: 'Washington' }
  ];

  const handleInputChange = (field, value) => {
    if (field?.includes('.')) {
      const [parent, child] = field?.split('.');
      setBillingData(prev => ({
        ...prev,
        [parent]: {
          ...prev?.[parent],
          [child]: value
        }
      }));
    } else {
      setBillingData(prev => ({ ...prev, [field]: value }));
    }

    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleCardElementChange = (elementType) => (event) => {
    if (event?.error) {
      setCardErrors(prev => ({
        ...prev,
        [elementType]: event?.error?.message
      }));
    } else {
      setCardErrors(prev => ({
        ...prev,
        [elementType]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!billingData?.cardholderName?.trim()) {
      newErrors.cardholderName = 'Cardholder name is required';
    }

    if (!billingData?.billingAddress?.street?.trim()) {
      newErrors['billingAddress.street'] = 'Street address is required';
    }

    if (!billingData?.billingAddress?.city?.trim()) {
      newErrors['billingAddress.city'] = 'City is required';
    }

    if (!billingData?.billingAddress?.zipCode?.trim()) {
      newErrors['billingAddress.zipCode'] = 'ZIP code is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    if (!validateForm()) {
      return;
    }

    const cardNumberElement = elements?.getElement(CardNumberElement);
    
    try {
      const { error, paymentMethod } = await stripe?.createPaymentMethod({
        type: 'card',
        card: cardNumberElement,
        billing_details: {
          name: billingData?.cardholderName,
          address: {
            line1: billingData?.billingAddress?.street,
            city: billingData?.billingAddress?.city,
            state: billingData?.billingAddress?.state,
            postal_code: billingData?.billingAddress?.zipCode,
            country: billingData?.billingAddress?.country,
          },
        },
      });

      if (error) {
        setCardErrors({ general: error?.message });
        return;
      }

      // Pass payment data to parent component
      onSubmit({
        paymentMethod,
        billingData,
        selectedTier
      });

    } catch (error) {
      console.error('Payment processing error:', error);
      setCardErrors({ general: 'Payment processing failed. Please try again.' });
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#1f2937',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        '::placeholder': {
          color: '#9ca3af',
        },
        iconColor: '#6b7280',
      },
      invalid: {
        color: '#ef4444',
        iconColor: '#ef4444',
      },
    },
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Payment Method Header */}
      <div className="flex items-center space-x-3 pb-4 border-b border-border">
        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
          <Icon name="CreditCard" size={20} className="text-primary" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Payment Information</h3>
          <p className="text-sm text-muted-foreground">
            Secure payment processing with 256-bit SSL encryption
          </p>
        </div>
      </div>

      {/* Card Information */}
      <div className="space-y-4">
        <h4 className="text-sm font-medium text-foreground">Card Details</h4>
        
        {cardErrors?.general && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="AlertCircle" size={16} className="text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-800">{cardErrors?.general}</p>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Card Number
            </label>
            <div className="relative p-3 border border-input rounded-lg bg-background focus-within:ring-2 focus-within:ring-ring focus-within:border-transparent">
              <CardNumberElement 
                options={cardElementOptions}
                onChange={handleCardElementChange('cardNumber')}
              />
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <Icon name="CreditCard" size={20} className="text-muted-foreground" />
              </div>
            </div>
            {cardErrors?.cardNumber && (
              <p className="text-sm text-red-600 mt-1">{cardErrors?.cardNumber}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Expiry Date
              </label>
              <div className="p-3 border border-input rounded-lg bg-background focus-within:ring-2 focus-within:ring-ring focus-within:border-transparent">
                <CardExpiryElement 
                  options={cardElementOptions}
                  onChange={handleCardElementChange('cardExpiry')}
                />
              </div>
              {cardErrors?.cardExpiry && (
                <p className="text-sm text-red-600 mt-1">{cardErrors?.cardExpiry}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                CVC
              </label>
              <div className="p-3 border border-input rounded-lg bg-background focus-within:ring-2 focus-within:ring-ring focus-within:border-transparent">
                <CardCvcElement 
                  options={cardElementOptions}
                  onChange={handleCardElementChange('cardCvc')}
                />
              </div>
              {cardErrors?.cardCvc && (
                <p className="text-sm text-red-600 mt-1">{cardErrors?.cardCvc}</p>
              )}
            </div>
          </div>

          <Input
            label="Cardholder Name"
            type="text"
            placeholder="John Smith"
            value={billingData?.cardholderName}
            onChange={(e) => handleInputChange('cardholderName', e?.target?.value)}
            error={errors?.cardholderName}
            required
          />
        </div>
      </div>

      {/* Billing Address */}
      <div className="space-y-4">
        <h4 className="text-sm font-medium text-foreground">Billing Address</h4>
        
        <Input
          label="Street Address"
          type="text"
          placeholder="123 Main Street"
          value={billingData?.billingAddress?.street}
          onChange={(e) => handleInputChange('billingAddress.street', e?.target?.value)}
          error={errors?.['billingAddress.street']}
          required
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="City"
            type="text"
            placeholder="New York"
            value={billingData?.billingAddress?.city}
            onChange={(e) => handleInputChange('billingAddress.city', e?.target?.value)}
            error={errors?.['billingAddress.city']}
            required
          />

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">State</label>
            <Select
              options={stateOptions}
              value={billingData?.billingAddress?.state}
              onChange={(value) => handleInputChange('billingAddress.state', value)}
              placeholder="Select state"
              searchable
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="ZIP Code"
            type="text"
            placeholder="10001"
            value={billingData?.billingAddress?.zipCode}
            onChange={(e) => handleInputChange('billingAddress.zipCode', e?.target?.value)}
            error={errors?.['billingAddress.zipCode']}
            required
          />

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Country</label>
            <Select
              options={countryOptions}
              value={billingData?.billingAddress?.country}
              onChange={(value) => handleInputChange('billingAddress.country', value)}
              searchable
            />
          </div>
        </div>
      </div>

      {/* Payment Options */}
      <div className="space-y-4 pt-4 border-t border-border">
        <Checkbox
          label="Save this card for future payments"
          description="Securely store your payment method for easy renewals"
          checked={billingData?.saveCard}
          onChange={(e) => handleInputChange('saveCard', e?.target?.checked)}
        />

        <Checkbox
          label="Enable automatic renewal"
          description="Automatically renew your subscription to avoid service interruption"
          checked={billingData?.autoRenew}
          onChange={(e) => handleInputChange('autoRenew', e?.target?.checked)}
        />
      </div>

      {/* Security Notice */}
      <div className="bg-muted/50 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Shield" size={20} className="text-success flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-foreground mb-1">
              Secure Payment Processing
            </h4>
            <p className="text-xs text-muted-foreground">
              Your payment information is encrypted and processed securely. We never store your full card details on our servers.
            </p>
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <Button
        type="submit"
        variant="default"
        size="lg"
        fullWidth
        loading={isLoading}
        disabled={!stripe || isLoading}
        iconName="Lock"
        iconPosition="left"
        className="mt-8"
      >
        Complete Registration
      </Button>

      {/* Payment Methods */}
      <div className="text-center pt-4">
        <p className="text-xs text-muted-foreground mb-3">We accept</p>
        <div className="flex items-center justify-center space-x-4">
          <Icon name="CreditCard" size={24} className="text-muted-foreground" />
          <Icon name="CreditCard" size={24} className="text-muted-foreground" />
          <Icon name="CreditCard" size={24} className="text-muted-foreground" />
          <Icon name="CreditCard" size={24} className="text-muted-foreground" />
        </div>
      </div>
    </form>
  );
};

const PaymentForm = ({ selectedTier, billingCycle, onSubmit, isLoading }) => {
  const { stripe, loading, error, isReady } = useStripeContext();

  if (error) {
    return (
      <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div className="flex items-center space-x-3">
          <Icon name="AlertTriangle" size={20} className="text-yellow-600 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-medium text-yellow-800">Payment System Unavailable</h4>
            <p className="text-sm text-yellow-700 mt-1">
              We're experiencing temporary issues with our payment system. Please try again in a few minutes.
            </p>
            <p className="text-xs text-yellow-600 mt-2">Error: {error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading || !isReady) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-sm text-muted-foreground">Loading secure payment form...</p>
        </div>
      </div>
    );
  }

  if (!stripe) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex items-center space-x-3">
          <Icon name="AlertCircle" size={20} className="text-red-600 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-medium text-red-800">Payment Configuration Error</h4>
            <p className="text-sm text-red-700 mt-1">
              Unable to initialize payment system. Please contact support if this issue persists.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Elements stripe={stripe}>
      <StripeCardForm 
        onSubmit={onSubmit}
        isLoading={isLoading}
        selectedTier={selectedTier}
      />
    </Elements>
  );
};

export default PaymentForm;